import React, { useEffect, useState } from 'react'
import moment from 'moment-timezone'
import { useMutation } from '@apollo/client'
import {
  Avatar,
  Form,
  Row,
  Col,
  Input,
  Upload,
  Button,
  Select,
  Switch,
  PageHeader
} from 'antd'
import { FileImageOutlined, LoadingOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { UPLOAD_IMAGE } from '../ProductQueries'

import { ProductDetailProps, CategoryInfo } from '@slerp/controls'
import { FormInstance } from 'antd/lib/form/Form'
import { useSession } from '@slerp/accounts'
import env from '../../../env'

interface ProductFormProps {
  productDetails: ProductDetailProps
  onFinish: () => void
  form: FormInstance
  handleChange: (
    target: string,
    value: string | number | boolean | null
  ) => void
  handleNewCategory: () => void
  categories: Array<CategoryInfo>
  newCategory: boolean
  setNewCategory: (value: boolean) => void
  orderSwitch: boolean
  setOrderSwitch: (value: boolean) => void
  refetchProducts: () => void
}

const ProductForm = (props: ProductFormProps) => {
  const {
    productDetails,
    onFinish,
    form,
    handleChange,
    handleNewCategory,
    categories,
    newCategory,
    setNewCategory,
    orderSwitch,
    setOrderSwitch,
    refetchProducts
  } = props
  const { merchant } = useSession()
  const [variantImageUrl, setVariantImageUrl] = useState('')
  const [imageUploadLoading, setImageUploadLoading] = useState(false)
  const [uploadImage] = useMutation(UPLOAD_IMAGE, {
    fetchPolicy: 'no-cache'
  })

  useEffect(() => {
    if (productDetails) {
      setVariantImageUrl(
        `https://${merchant.slug}.${env.ASSET_HOST}/assets/variant/${
          productDetails.variant_id
        }?size=original&${moment().unix()}`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDetails])

  const beforeImageUpload = (file: any) => {
    setImageUploadLoading(true)
    return true // this always expects a boolean return value
  }

  const ImageUploader = () => {
    return (
      <Upload
        name='avatar'
        accept='.png, .jpg, .jpeg'
        listType='picture-card'
        className='avatar-uploader'
        showUploadList={false}
        customRequest={({ file, onSuccess, onError }) =>
          uploadImage({
            variables: {
              variantId: productDetails.variant_id,
              image: file
            }
          })
            .then((res) => {
              setImageUploadLoading(false)
              onSuccess(res, file)
              refetchProducts()
            })
            .catch((error) => onError(error))
        }
        beforeUpload={beforeImageUpload}
      >
        {productDetails && productDetails.image && !imageUploadLoading ? (
          <Avatar size={90} shape='square' src={variantImageUrl} />
        ) : imageUploadLoading ? (
          <LoadingOutlined />
        ) : (
          <FileImageOutlined />
        )}
      </Upload>
    )
  }

  return (
    <Form form={form} onFinish={onFinish}>
      <Row>
        <PageHeader title='Product' style={{ paddingBottom: '0' }} />
      </Row>
      <Row style={{ marginBottom: '20px' }}>
        <label>
          Modify product name, description, category, price, images and
          restrictions
        </label>
      </Row>
      <Row gutter={24} style={{ margin: '15px' }}>
        <Col span={12}>
          <Row style={{ marginBottom: '24px' }}>
            <Input
              data-testid='name-field'
              placeholder='Enter product name'
              autoFocus
              required
              size='large'
              value={productDetails.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </Row>
          <Row style={{ marginBottom: '24px' }}>
            <Input.TextArea
              placeholder='Enter description'
              style={{ fontSize: '16px' }}
              autoSize={{ minRows: 4, maxRows: 5 }}
              value={productDetails.description}
              onChange={(e) => handleChange('description', e.target.value)}
            />
          </Row>
          <Row style={{ marginBottom: '24px' }}>
            {newCategory || categories.length === 0 ? (
              <>
                <Col
                  style={{
                    marginRight: '8px',
                    width: productDetails.category_name ? '63%' : '81.3%',
                    transition: '.2s'
                  }}
                >
                  <Input
                    type='text'
                    autoFocus
                    size='large'
                    placeholder='Enter new category name'
                    onChange={(e) =>
                      handleChange('category_name', e.target.value)
                    }
                    required
                  />
                </Col>
                {productDetails.category_name && (
                  <Button
                    size='large'
                    onClick={() => handleNewCategory()}
                    style={{ marginRight: '8px' }}
                  >
                    Create
                  </Button>
                )}
                {categories.length !== 0 && (
                  <Button
                    size='large'
                    onClick={() => {
                      setNewCategory(!newCategory)
                      handleChange('category_name', null)
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </>
            ) : (
              <>
                <Col span={12}>
                  <Select
                    value={productDetails.category_id}
                    onChange={(value) => handleChange('category_id', value)}
                    size='large'
                    style={{ width: '100%' }}
                  >
                    <Select.Option value='select' disabled>
                      Select a category
                    </Select.Option>
                    {categories.map(
                      ({ key, name }: { key: string; name: string }) => (
                        <Select.Option value={key} key={key}>
                          {name}
                        </Select.Option>
                      )
                    )}
                  </Select>
                </Col>
                <label style={{ margin: '5px 15px 0' }}>or</label>
                <Col>
                  <Button
                    size='large'
                    onClick={() => setNewCategory(!newCategory)}
                  >
                    Add category
                  </Button>
                </Col>
              </>
            )}
          </Row>
          <Row gutter={8} style={{ marginBottom: '24px' }}>
            <Col style={{ width: '50%' }}>
              <Input
                placeholder='Enter price'
                required
                min='0.0'
                step='0.01'
                type='number'
                addonBefore='£'
                size='large'
                value={productDetails.default_price}
                onChange={(e) =>
                  handleChange('default_price', parseFloat(e.target.value))
                }
              />
            </Col>
            <Col style={{ width: '50%' }}>
              <Input
                placeholder='Enter VAT'
                required
                min='0.0'
                step='0.01'
                type='number'
                addonAfter='%'
                size='large'
                value={productDetails.default_vat}
                onChange={(e) =>
                  handleChange('default_vat', parseFloat(e.target.value))
                }
              />
            </Col>
          </Row>
        </Col>
        <Col span={12}>
          <Row>{ImageUploader()}</Row>
        </Col>
      </Row>
      <Row gutter={24} style={{ margin: '15px' }}>
        <Col span={12}>
          <Row style={{ marginBottom: '24px' }}>
            <Switch
              title='Order limit'
              onChange={() => setOrderSwitch(!orderSwitch)}
              checked={orderSwitch}
            />
            <label style={{ marginLeft: '8px' }}>Order Limit</label>
          </Row>
          {orderSwitch && (
            <Row style={{ marginBottom: '24px' }}>
              <Col>
                <Input
                  min='0'
                  type='number'
                  size='large'
                  autoFocus={!orderSwitch}
                  value={productDetails.default_limit}
                  onChange={(e) =>
                    handleChange('default_limit', e.target.value)
                  }
                />
              </Col>
              <span>
                Limit the number of times a customer can add this product to
                their cart.
              </span>
            </Row>
          )}
          <Row style={{ marginBottom: '24px' }}>
            <Switch
              title='Age limit'
              onChange={(e) => handleChange('alcoholic', e)}
              checked={productDetails.alcoholic}
            />
            <label style={{ marginLeft: '8px' }}>Age Limit</label>
          </Row>
        </Col>
        <Col span={12}>
          <Row style={{ marginBottom: '24px' }}>
            <Switch
              title='Publish to store'
              onChange={(e) => handleChange('published', e)}
              checked={productDetails.published}
              style={{ marginBottom: '10px' }}
            />
            <label style={{ marginLeft: '8px' }}>Publish to Stores</label>
            {productDetails.published && (
              <Row>
                <span style={{ color: '#FF0000' }}>
                  <b>Remember to activate the product in your store settings</b>
                </span>
                <span style={{ color: '#FF0000' }}>
                  This will make the product available for purchase from your
                  chosen stores.
                </span>
              </Row>
            )}
          </Row>
        </Col>
      </Row>
      <hr
        style={{
          borderColor: '#e8e8e8',
          borderStyle: 'none none solid',
          margin: '24px 0',
          width: '100%'
        }}
      />
      <Row justify='end'>
        <Button size='large' key='cancel' type='text'>
          <Link to='/products'>Cancel</Link>
        </Button>
        <Button size='large' type='primary' htmlType='submit'>
          Save
        </Button>
      </Row>
    </Form>
  )
}

export default ProductForm
